import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export function PageShell({ children, showFooter = true }: { children: ReactNode; showFooter?: boolean }) {
  return (
    <div className="flex min-h-screen flex-1 flex-col">
      <main className="flex flex-1 flex-col">{children}</main>
      {/* Reserved, deliberately quiet: no platform name is confirmed yet, so this
       * closing rule marks where a future "powered by" credit can sit without
       * competing with the retailer's own identity above it. */}
      {showFooter && (
        <footer className="px-5 py-6" aria-hidden="true">
          <div className="mx-auto h-px max-w-[640px] bg-border" />
        </footer>
      )}
    </div>
  );
}

/** The authenticated workspace's content region: the column that sits beside the navigation
 * column and carries the active destination. The navigation column owns the page's left edge and
 * the full height of the workspace body, so every horizontal and vertical rhythm the old centred
 * container held now belongs here — capped at 1200px so the pair still reads as one 1440px
 * workspace. Also used on its own by the states that have no navigation yet (loading, access
 * errors, an unassigned account). */
export function WorkspacePane({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className="flex min-w-0 flex-1 flex-col">
      <div
        className={cn(
          "mx-auto flex w-full max-w-[1200px] flex-1 flex-col gap-5",
          "px-[clamp(var(--space-5),4vw,var(--space-10))] pt-6 pb-12 md:pt-8",
          className,
        )}
      >
        {children}
      </div>
    </div>
  );
}
